import { catchSentryError } from "@utils/sentry";
import { fetchAuthSession } from "aws-amplify/auth";

import { BUCKET_NAME, LOCALSTACK_S3_ENDPOINT } from "@app/bootstrap/init";

export const STORAGE_BUCKET = {
  bucketName: BUCKET_NAME,
  region: "ap-southeast-2"
} as const;

/**
 * Spread into the `options` of every Amplify storage call. Empty for normal builds;
 * under `pnpm run dev:localstack` it redirects S3 at the LocalStack container.
 *
 * Per-call rather than global because Amplify's global escape hatch
 * (`dangerouslyConnectToHttpEndpointForTesting`) is hardwired to Amplify Mock's port
 * and throws InvalidCustomEndpoint regardless — aws-amplify/amplify-js#13961.
 */
export const STORAGE_ENDPOINT = LOCALSTACK_S3_ENDPOINT
  ? ({ customEndpoint: LOCALSTACK_S3_ENDPOINT } as const)
  : ({} as const);

export const UPLOAD_CACHE_CONTROL = "public, max-age=31536000, immutable";

const MAX_AUTH_RETRIES = 2;

export const isEmptyStorageKey = (key: string): boolean =>
  !key || key.length === 0;

export const isTokenError = (error: unknown): boolean => {
  const err = error as { message?: string; code?: string; name?: string };
  const message = err?.message?.toLowerCase() || "";
  const code = err?.code || err?.name || "";
  return (
    code === "NotAuthorizedException" ||
    code === "ExpiredToken" ||
    message.includes("expired") ||
    message.includes("token") ||
    message.includes("not authenticated") ||
    message.includes("no current user")
  );
};

export const forceRefreshAuthSession = async (): Promise<void> => {
  try {
    await fetchAuthSession({ forceRefresh: true });
  } catch (refreshError) {
    catchSentryError(refreshError);
    throw new Error("Unable to refresh authentication. Please log in again.");
  }
};

export const validateAuthForStorage = async (
  isRetry: boolean
): Promise<void> => {
  if (isRetry) {
    await forceRefreshAuthSession();
    return;
  }
  await fetchAuthSession();
};

export const withStorageAuthRetry = async <T>(
  operation: () => Promise<T>
): Promise<T> => {
  let attempt = 0;

  while (attempt <= MAX_AUTH_RETRIES) {
    try {
      await validateAuthForStorage(attempt > 0);
      return await operation();
    } catch (error) {
      attempt++;

      if (!isTokenError(error) || attempt > MAX_AUTH_RETRIES) {
        catchSentryError(error);
        throw error;
      }
    }
  }

  throw new Error("Storage operation failed after maximum retries");
};

export const handleStorageError = (error: unknown): never => {
  catchSentryError(error);
  throw error;
};
